import * as React from "react"

import { Slot } from "@/components/ui/internal"
import { buttonClassNames } from "@/components/ui/uiClasses"
import { cn } from "@/lib/utils"

type ButtonVariant = "default" | "destructive" | "outline" | "secondary" | "ghost" | "link"
type ButtonSize = "default" | "sm" | "lg" | "icon" | "icon-sm" | "icon-lg"

const BUTTON_VARIANT_CLASSES: Record<ButtonVariant, string> = {
  default: buttonClassNames.variants.default,
  destructive: buttonClassNames.variants.destructive,
  outline: buttonClassNames.variants.outline,
  secondary: buttonClassNames.variants.secondary,
  ghost: buttonClassNames.variants.ghost,
  link: buttonClassNames.variants.link,
}

const BUTTON_SIZE_CLASSES: Record<ButtonSize, string> = {
  default: buttonClassNames.sizes.default,
  sm: buttonClassNames.sizes.sm,
  lg: buttonClassNames.sizes.lg,
  icon: buttonClassNames.sizes.icon,
  "icon-sm": buttonClassNames.sizes["icon-sm"],
  "icon-lg": buttonClassNames.sizes["icon-lg"],
}

function buttonVariants({
  variant = "default",
  size = "default",
  className,
}: {
  variant?: ButtonVariant
  size?: ButtonSize
  className?: string
}) {
  return cn(
    buttonClassNames.base,
    BUTTON_VARIANT_CLASSES[variant],
    BUTTON_SIZE_CLASSES[size],
    className
  )
}

const Button = React.forwardRef<
  HTMLElement,
  React.ComponentProps<"button"> & {
    variant?: ButtonVariant
    size?: ButtonSize
    asChild?: boolean
  }
>(function Button({
  className,
  variant = "default",
  size = "default",
  asChild = false,
  children,
  ...props
}, ref) {
  const classes = cn(buttonVariants({ variant, size, className }))

  if (asChild) {
    return (
      <Slot
        ref={ref}
        data-slot="button"
        data-variant={variant}
        className={classes}
        {...props}
      >
        {children as React.ReactElement}
      </Slot>
    )
  }

  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      data-slot="button"
      data-variant={variant}
      className={classes}
      type={props.type ?? "button"}
      {...props}
    >
      {children}
    </button>
  )
})

export { Button, buttonVariants }
